import {
  GetHistoricalSpendsResponse,
  HistoricalSpendActive,
} from "@mutiny-group/sbo-openapi-client";
import { useQuery } from "@tanstack/react-query";

import { INSIGHTS_BACKEND_URL } from "@/lib/axios";
import { QueryConfig } from "@/lib/react-query";
import { axiosApi } from "@/Utils";

import { useScenarioBuilderClient } from "../../api/hooks/useScenarioBuilderClient";
import { getHistoricalSpendsV2 } from "./v2/getHistoricalSpends";

export type SpendNode = {
  customer: string;
  division: string;
  brand: string;
  category: string;
  product: string;
  geography: string | null;
};

export type HistoricalSpendDTO = {
  marketing_spend: {
    channel: string;
    marketing_spend_node: SpendNode;
    spend: number;
  };
  is_node_channel_active: boolean;
};

export const getHistoricalSpends = (
  startDate?: string,
  endDate?: string,
  brand?: string | null,
  geographies?: string[]
): Promise<HistoricalSpendDTO[]> => {
  return axiosApi.get("/api/v2/scenarios-new/historical-spend/v2", {
    baseURL: INSIGHTS_BACKEND_URL,
    params: {
      startDate,
      endDate,
      brand,
      geographies,
    },
  });
};

type QueryFnType = typeof getHistoricalSpends | typeof getHistoricalSpendsV2;

type UseHistoricalSpendsOptions = {
  config?: QueryConfig<QueryFnType>;
  startDate: string | undefined;
  endDate: string | undefined;
  brand: string | undefined;
  geographies: string[] | undefined;
  isScenariosApiEnabled?: boolean;
};

export const useHistoricalSpends = ({
  config,
  startDate,
  endDate,
  brand,
  geographies,
  isScenariosApiEnabled = false,
}: UseHistoricalSpendsOptions) => {
  const scenarioBuilderClient = useScenarioBuilderClient();
  const newBrand = brand === "default" ? null : brand;

  return useQuery({
    ...config,
    queryKey: [
      `historical_spends`,
      startDate,
      endDate,
      newBrand,
      JSON.stringify(geographies),
      isScenariosApiEnabled,
    ],
    queryFn: isScenariosApiEnabled
      ? () =>
          getHistoricalSpendsV2({
            scenarioBuilderClient,
            brand: newBrand,
            geographies: geographies ?? null,
            scenarioStartDate: startDate ? new Date(startDate) : null,
            scenarioEndDate: endDate ? new Date(endDate) : null,
          })
      : () => getHistoricalSpends(startDate, endDate, newBrand, geographies),
    select: (data) => {
      if (isScenariosApiEnabled) {
        return (data as GetHistoricalSpendsResponse).historicalSpends.map((historicalSpend) =>
          transformHistoricalSpend(historicalSpend)
        );
      }
      return data as HistoricalSpendDTO[];
    },
  });
};

const transformHistoricalSpend = (historicalSpend: HistoricalSpendActive): HistoricalSpendDTO => ({
  marketing_spend: {
    channel: historicalSpend.marketingSpend.channel,
    marketing_spend_node: {
      customer: historicalSpend.marketingSpend.marketingSpendNode.customer,
      division: historicalSpend.marketingSpend.marketingSpendNode.division || "",
      brand: historicalSpend.marketingSpend.marketingSpendNode.brand,
      category: historicalSpend.marketingSpend.marketingSpendNode.category,
      product: historicalSpend.marketingSpend.marketingSpendNode.product,
      geography: historicalSpend.marketingSpend.geography ?? null,
    },
    spend: historicalSpend.marketingSpend.spend,
  },
  is_node_channel_active: historicalSpend.isNodeChannelActive,
});
